import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Button } from 'tio-ui/components/buttons';
import { t, type IntlService } from 'ember-intl';
import { PhSparkle, PhArrowRight, PhTarget, PhBookOpen } from 'ember-phosphor-icons';
import StatePill from 'tio-employee/components/reimagined/tas/state-pill';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type {
  DataSourceInfo,
  LiveTasApplication,
  LiveTasData,
  LiveTasProgram,
} from 'tio-employee/services/reimagined-data';

interface TasIndexModel {
  tasData: LiveTasData;
  tasUsedAmount: { yearToDate: number; isLiveData: boolean };
  dataSourceInfo: DataSourceInfo;
}

interface TasIndexSignature {
  Args: {
    model: TasIndexModel;
  };
}

export default class CareerPathwaysTasIndex extends Component<TasIndexSignature> {
  @service declare intl: IntlService;
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get programs(): LiveTasProgram[] {
    return this.args.model.tasData.programs;
  }

  get applications(): LiveTasApplication[] {
    return this.args.model.tasData.applications;
  }

  formatCurrency = (value: number) =>
    this.intl.formatNumber(value, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });

  <template>
    <VStack class="gap-8">
      <div
        class="rounded-2xl bg-gradient-to-br from-sky-500 via-indigo-500 to-violet-600 p-6 text-white shadow-lg"
      >
        <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div class="max-w-2xl">
            <div
              class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/80"
            >
              <PhSparkle class="h-4 w-4" />
              {{t "tuition_assistance.reimagined.index.hero_eyebrow"}}
            </div>
            <Heading @level={{1}} class="text-white">
              {{t "tuition_assistance.reimagined.index.hero_title"}}
            </Heading>
            <Text class="text-white/80">
              {{t "tuition_assistance.reimagined.index.hero_subtitle"}}
            </Text>
          </div>
          <div class="flex flex-wrap gap-3">
            <LinkTo @route="authenticated.tas.dashboard">
              <Button @intent="primary">{{t
                  "tuition_assistance.reimagined.index.cta_start"
                }}</Button>
            </LinkTo>
            <LinkTo @route="authenticated.reimagined.tas.programs">
              <Button
                @intent="primary"
                @appearance="outlined"
                class="border-white/50 text-white bg-white/15 hover:bg-white/25"
              >
                {{t "tuition_assistance.reimagined.index.cta_explore"}}
              </Button>
            </LinkTo>
          </div>
        </div>
      </div>

      <DataIndicator @source={{this.tasSource}} @showBadge={{true}} @dataSourceId="tas-overview">
        <Section>
          <:header>
            <div class="flex items-center justify-between">
              <Heading @level={{2}}>{{t
                  "tuition_assistance.reimagined.index.section_overview"
                }}</Heading>
              <LinkTo @route="authenticated.tas.dashboard" class="tio-copy text-sm font-semibold">
                {{t "tuition_assistance.reimagined.index.link_view_dashboard"}}
              </LinkTo>
            </div>
          </:header>
          <:body>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div class="flex items-center justify-between">
                  <Text @intent="muted">{{t
                      "tuition_assistance.reimagined.index.card_approved"
                    }}</Text>
                  <PhTarget class="h-5 w-5 text-indigo-500" />
                </div>
                <Heading @level={{3}}>
                  {{this.formatCurrency @model.tasData.summary.totalApproved}}
                </Heading>
              </div>
              <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div class="flex items-center justify-between">
                  <Text @intent="muted">{{t
                      "tuition_assistance.reimagined.index.card_pending"
                    }}</Text>
                  <PhBookOpen class="h-5 w-5 text-amber-500" />
                </div>
                <Heading @level={{3}}>
                  {{@model.tasData.summary.pendingCount}}
                </Heading>
              </div>
              <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div class="flex items-center justify-between">
                  <Text @intent="muted">{{t "tuition_assistance.reimagined.index.card_used"}}</Text>
                  <PhSparkle class="h-5 w-5 text-fuchsia-500" />
                </div>
                <Heading @level={{3}}>
                  {{this.formatCurrency @model.tasUsedAmount.yearToDate}}
                </Heading>
              </div>
            </div>
          </:body>
        </Section>
      </DataIndicator>

      <Section>
        <:header>
          <div class="flex items-center justify-between">
            <Heading @level={{2}}>{{t
                "tuition_assistance.reimagined.index.section_programs"
              }}</Heading>
            <LinkTo
              @route="authenticated.reimagined.tas.programs"
              class="tio-copy text-sm font-semibold"
            >
              {{t "tuition_assistance.reimagined.index.link_view_programs"}}
            </LinkTo>
          </div>
        </:header>
        <:body>
          {{#if this.programs.length}}
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {{#each this.programs as |program|}}
                <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div class="flex items-start justify-between gap-4">
                    <div>
                      <Heading @level={{3}}>{{program.name}}</Heading>
                      <Text @intent="muted">{{program.institution}}</Text>
                      {{#if program.startDate}}
                        <Text @intent="muted">
                          {{t
                            "tuition_assistance.reimagined.index.label_starts"
                            date=program.startDate
                          }}
                        </Text>
                      {{/if}}
                    </div>
                    <StatePill @state={{program.state}} />
                  </div>
                  <div class="mt-4">
                    <LinkTo
                      @route="authenticated.tas.programs.show"
                      @model={{program.id}}
                      class="tio-copy text-sm font-semibold"
                    >
                      {{t "tuition_assistance.reimagined.index.link_open_program"}}
                      <PhArrowRight class="ml-1 inline h-4 w-4" />
                    </LinkTo>
                  </div>
                </div>
              {{/each}}
            </div>
          {{else}}
            <div class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8">
              <Heading @level={{3}}>{{t
                  "tuition_assistance.reimagined.index.empty_programs_title"
                }}</Heading>
              <Text @intent="muted">
                {{t "tuition_assistance.reimagined.index.empty_programs_body"}}
              </Text>
              <div class="mt-4">
                <LinkTo @route="authenticated.tas.dashboard">
                  <Button @intent="primary">{{t
                      "tuition_assistance.reimagined.index.empty_programs_cta"
                    }}</Button>
                </LinkTo>
              </div>
            </div>
          {{/if}}
        </:body>
      </Section>

      <Section>
        <:header>
          <div class="flex items-center justify-between">
            <Heading @level={{2}}>{{t
                "tuition_assistance.reimagined.index.section_applications"
              }}</Heading>
            <LinkTo
              @route="authenticated.reimagined.tas.applications"
              class="tio-copy text-sm font-semibold"
            >
              {{t "tuition_assistance.reimagined.index.link_view_applications"}}
            </LinkTo>
          </div>
        </:header>
        <:body>
          {{#if this.applications.length}}
            <div class="grid grid-cols-1 gap-4">
              {{#each this.applications as |application|}}
                <div class="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                  <div class="flex items-start justify-between gap-4">
                    <div>
                      <Heading @level={{3}}>{{application.name}}</Heading>
                      <Text @intent="muted">{{application.programName}}</Text>
                      {{#if application.coursesBeginDate}}
                        <Text @intent="muted">
                          {{t
                            "tuition_assistance.reimagined.index.label_starts"
                            date=application.coursesBeginDate
                          }}
                        </Text>
                      {{/if}}
                    </div>
                    <div class="flex items-center gap-2">
                      <StatePill @state={{application.state}} />
                      <StatePill @state={{application.paymentState}} />
                    </div>
                  </div>
                  <div class="mt-4 flex items-center gap-4">
                    <Text @intent="muted">
                      {{t
                        "tuition_assistance.reimagined.index.label_approved"
                        amount=(this.formatCurrency application.approvedTotal)
                      }}
                    </Text>
                    <LinkTo
                      @route="authenticated.tas.applications.show"
                      @model={{application.id}}
                      class="tio-copy text-sm font-semibold"
                    >
                      {{t "tuition_assistance.reimagined.index.link_review"}}
                      <PhArrowRight class="ml-1 inline h-4 w-4" />
                    </LinkTo>
                  </div>
                </div>
              {{/each}}
            </div>
          {{else}}
            <div class="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-8">
              <Heading @level={{3}}>{{t
                  "tuition_assistance.reimagined.index.empty_applications_title"
                }}</Heading>
              <Text @intent="muted">
                {{t "tuition_assistance.reimagined.index.empty_applications_body"}}
              </Text>
              <div class="mt-4">
                <LinkTo @route="authenticated.tas.dashboard">
                  <Button @intent="primary">{{t
                      "tuition_assistance.reimagined.index.empty_applications_cta"
                    }}</Button>
                </LinkTo>
              </div>
            </div>
          {{/if}}
        </:body>
      </Section>
    </VStack>
  </template>
}
